import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import moment from 'moment';
import { useEffect, useRef, useState } from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../Tooltip';
import { styleSheet } from './styles';

interface IContactRenewalPoliciesProps {
	policies: Api.ISparsePolicy[];
}

/**
 * Renders a "X policies renewing" link for a contact row in the UpcomingPolicyRenewals flow.
 * Hover previews the policy list; clicking pins the popover open so touch users and reviewers
 * who instinctively click the link also get the details.
 */
export function ContactRenewalPolicies({ policies }: IContactRenewalPoliciesProps) {
	const [isPinned, setIsPinned] = useState(false);
	const containerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!isPinned) {
			return;
		}
		const dismissOnOutsideClick = (event: MouseEvent) => {
			const target = event.target as Element | null;
			if (!target || containerRef.current?.contains(target) || target.closest('[data-radix-popper-content-wrapper]')) {
				return;
			}
			setIsPinned(false);
		};
		const dismissOnEscape = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				setIsPinned(false);
			}
		};
		document.addEventListener('mousedown', dismissOnOutsideClick);
		document.addEventListener('keydown', dismissOnEscape);
		return () => {
			document.removeEventListener('mousedown', dismissOnOutsideClick);
			document.removeEventListener('keydown', dismissOnEscape);
		};
	}, [isPinned]);

	if (!policies || policies.length === 0) {
		return null;
	}

	const label = policies.length === 1 ? '1 policy renewing' : `${policies.length} policies renewing`;

	return (
		<div ref={containerRef} onClick={e => e.stopPropagation()}>
			<TooltipProvider>
				<Tooltip open={isPinned ? true : undefined}>
					<TooltipTrigger asChild>
						<button type='button' className={css(styleSheet.trigger)} onClick={() => setIsPinned(current => !current)}>
							{label}
						</button>
					</TooltipTrigger>
					<TooltipContent side='bottom' align='start' styles={[styleSheet.tooltipContent]}>
						<div className={css(styleSheet.policyList)}>
							<div className={css(styleSheet.tooltipHeader)}>Renewing Policies</div>
							{policies.map(policy => (
								<div className={css(styleSheet.policyCard)} key={policy.id}>
									<div className={css(styleSheet.policyLob)}>{policy.lineOfBusiness || 'Unknown LOB'}</div>
									<div className={css(styleSheet.policyDetails)}>
										{policy.carrier ? (
											<span className={css(styleSheet.policyDetail)}>
												<span className={css(styleSheet.detailLabel)}>Carrier:</span> {policy.carrier}
											</span>
										) : null}
										{policy.renewalDate ? (
											<span className={css(styleSheet.policyDetail)}>
												<span className={css(styleSheet.detailLabel)}>Renews:</span>{' '}
												{moment.utc(policy.renewalDate).format('MMM D, YYYY')}
											</span>
										) : null}
										{policy.premium != null ? (
											<span className={css(styleSheet.policyDetail)}>
												<span className={css(styleSheet.detailLabel)}>Premium:</span> ${policy.premium.toLocaleString()}
												/yr
											</span>
										) : null}
										{policy.policyNumber ? (
											<span className={css(styleSheet.policyDetail)}>
												<span className={css(styleSheet.detailLabel)}>Policy #:</span> {policy.policyNumber}
											</span>
										) : null}
									</div>
								</div>
							))}
						</div>
					</TooltipContent>
				</Tooltip>
			</TooltipProvider>
		</div>
	);
}
